import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './OJTCoordinator.css';
import { FaHome, FaChartLine, FaUser, FaUsers, FaMapMarkerAlt, FaComments } from 'react-icons/fa';
import { FaArrowRightFromBracket } from 'react-icons/fa6';
import { useAuth } from '../../lib/AuthProvider';
import TraineeList from './Trainees/TraineeList';
import { supabase } from '../../lib/supabaseClient';
import { StudentProfile } from '../../lib/database.types';
import { TotalStudentsCard } from './ProgramStat/TotalStudents';
import { ActiveStudentsCard, ActiveStudentsModal } from './ProgramStat/ActiveStudents';
import { AtRiskStudentsCard, AtRiskStudentsModal, WeeklyTrainee } from './ProgramStat/AtRiskStudents';
import { ClassSectionsCard, ClassSectionsModal } from './ProgramStat/ClassSections';

interface WeeklyAttendanceSummary {
  id: string;
  student_id: string;
  week_number: number;
  scheduled_days: number;
  days_present: number;
  days_absent: number;
  noclass_days: number;
  avg_hours_per_day: number;
  late_minutes: number;
  created_at: string;
  updated_at: string;
}

const OJTCoordinatorHome: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState({ firstName: '', lastName: '' });
  const [loading, setLoading] = useState(true);
  
  // Determine activeView based on current route
  const getActiveView = (pathname: string): 'home' | 'trainees' => {
    if (pathname === '/ojt-coordinator/trainees') {
      return 'trainees';
    }
    return 'home';
  };
  
  const [activeView, setActiveView] = useState<'home' | 'trainees'>(getActiveView(location.pathname));
  
  // Update activeView when route changes
  useEffect(() => {
    setActiveView(getActiveView(location.pathname));
  }, [location.pathname]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showActiveStudentsModal, setShowActiveStudentsModal] = useState(false);
  const [showClassSectionsModal, setShowClassSectionsModal] = useState(false);
  const [showAtRiskStudentsModal, setShowAtRiskStudentsModal] = useState(false);
  const [studentsWithStatus, setStudentsWithStatus] = useState<Array<StudentProfile & { status: 'Active' | 'Inactive', lastCheckIn?: string }>>([]);
  const [studentsBySection, setStudentsBySection] = useState<{ [section: string]: StudentProfile[] }>({});
  const [weeklyTrainees, setWeeklyTrainees] = useState<WeeklyTrainee[]>([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    atRiskStudents: 0,
    totalHours: 0
  });

  // Function to call the batch risk prediction API
  const predictRiskBatch = async (batchData: Array<{student_id: string, week_number: number, weekly_data: WeeklyAttendanceSummary}>): Promise<Map<string, {risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}>> => {
    try {
      const response = await fetch('http://localhost:5000/predict_batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch: batchData.map(item => ({
            student_id: item.student_id,
            week_number: item.week_number,
            weekly_data: {
              scheduled_days: item.weekly_data.scheduled_days,
              days_present: item.weekly_data.days_present,
              days_absent: item.weekly_data.days_absent,
              noclass_days: item.weekly_data.noclass_days,
              avg_hours_per_day: item.weekly_data.avg_hours_per_day,
              late_minutes: item.weekly_data.late_minutes
            }
          }))
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const predictionsMap = new Map<string, {risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}>();
      
      result.predictions.forEach((pred: any) => {
        const key = `${pred.student_id}_${pred.week_number}`;
        predictionsMap.set(key, {
          risk_label: pred.risk_label,
          attendance_rate: pred.attendance_rate,
          absent_rate: pred.absent_rate,
          holiday_ratio: pred.holiday_ratio
        });
      });
      
      return predictionsMap;
    } catch (error) {
      console.error('Error calling batch risk prediction API:', error);
      return new Map();
    }
  };

  // Fetch all students (for count only)
  const fetchStudents = async () => {
    try {
      const { count, error } = await supabase
        .from('student_profile')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error fetching students:', error);
      return 0;
    }
  };

  // Fetch weekly trainees with risk prediction
  const fetchWeeklyTrainees = async () => {
    try {
      // Fetch all student profiles and weekly attendance in parallel
      const [profilesResult, weeklyResult] = await Promise.all([
        supabase.from('student_profile').select('*'),
        supabase.from('weekly_attendance_summary').select('*').order('week_number', { ascending: false })
      ]);

      if (profilesResult.error) {
        console.error('Error fetching profiles:', profilesResult.error);
        return 0;
      }

      const profiles = profilesResult.data;
      const weeklyAttendance = weeklyResult.data;

      if (!profiles || profiles.length === 0) {
        setWeeklyTrainees([]);
        return 0;
      }

      if (weeklyResult.error) {
        console.error('Error fetching weekly attendance:', weeklyResult.error);
      }

      // Determine the current week (highest week_number)
      let currentWeek: number | null = null;
      const allWeekNumbers: number[] = [];
      if (weeklyAttendance && weeklyAttendance.length > 0) {
        const weekNumbers = new Set<number>();
        weeklyAttendance.forEach((attendance: WeeklyAttendanceSummary) => {
          weekNumbers.add(attendance.week_number);
        });
        const sortedWeeks = Array.from(weekNumbers).sort((a, b) => b - a);
        currentWeek = sortedWeeks.length > 0 ? sortedWeeks[0] : null;
        allWeekNumbers.push(...sortedWeeks);
      }

      // Build batch data for all students with attendance data
      const batchData: Array<{student_id: string, week_number: number, weekly_data: WeeklyAttendanceSummary}> = [];
      const weeklyTraineesData: WeeklyTrainee[] = [];
      
      for (const profile of (profiles || []) as StudentProfile[]) {
        for (const weekNumber of allWeekNumbers) {
          const studentWeeklyData = (weeklyAttendance || []).find(
            (attendance: WeeklyAttendanceSummary) => 
              attendance.student_id === profile.student_id && 
              attendance.week_number === weekNumber
          ) as WeeklyAttendanceSummary | undefined;

          if (studentWeeklyData) {
            batchData.push({
              student_id: profile.student_id,
              week_number: weekNumber,
              weekly_data: studentWeeklyData
            });
          }
        }
      }

      // Get all predictions in a single batch call
      const predictionsMap = batchData.length > 0 ? await predictRiskBatch(batchData) : new Map();

      // Build the weekly trainees list using the predictions
      for (const profile of (profiles || []) as StudentProfile[]) {
        for (const weekNumber of allWeekNumbers) {
          const studentWeeklyData = (weeklyAttendance || []).find(
            (attendance: WeeklyAttendanceSummary) => 
              attendance.student_id === profile.student_id && 
              attendance.week_number === weekNumber
          ) as WeeklyAttendanceSummary | undefined;

          if (studentWeeklyData) {
            const key = `${profile.student_id}_${weekNumber}`;
            const riskPrediction = predictionsMap.get(key) || { risk_label: '0', attendance_rate: 0, absent_rate: 0, holiday_ratio: 0 };
            const weeklyHours = studentWeeklyData.avg_hours_per_day;
            
            weeklyTraineesData.push({
              firstName: profile.first_name,
              lastName: profile.last_name,
              department: profile.department,
              weeklyHours: Math.round(weeklyHours * 10) / 10,
              status: riskPrediction.risk_label === '1' ? 'At Risk' : 'Not At Risk',
              riskScore: parseFloat(riskPrediction.risk_label),
              attendanceRate: riskPrediction.attendance_rate,
              absentRate: riskPrediction.absent_rate,
              holidayRatio: riskPrediction.holiday_ratio,
              weekNumber: weekNumber
            });
          } else {
            weeklyTraineesData.push({
              firstName: profile.first_name,
              lastName: profile.last_name,
              department: profile.department,
              weeklyHours: undefined,
              status: 'N/A' as const,
              weekNumber: weekNumber
            });
          }
        }
      }

      setWeeklyTrainees(weeklyTraineesData);
      
      // Count at-risk students from current week only (for the card display)
      const atRiskCount = weeklyTraineesData.filter(
        t => t.status === 'At Risk' && t.weekNumber === currentWeek
      ).length;
      return atRiskCount;
    } catch (error) {
      console.error('Error fetching weekly trainees:', error);
      return 0;
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (user && 'first_name' in user && 'last_name' in user) {
          setUserName({
            firstName: user.first_name,
            lastName: user.last_name
          });
        } else {
          const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
          if (storedProfile.first_name) {
            setUserName({
              firstName: storedProfile.first_name,
              lastName: storedProfile.last_name
            });
          }
        }
        
        // Run all fetch operations in parallel for better performance
        const [totalStudents, activeStudents, classSections, atRiskStudents] = await Promise.all([
          fetchStudents(),
          fetchStudentsWithStatus(),
          fetchStudentsBySection(),
          fetchWeeklyTrainees()
        ]);
        
        setStats({
          totalStudents: totalStudents,
          activeStudents: activeStudents,
          atRiskStudents: atRiskStudents,
          totalHours: classSections,
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Fetch students with their active/inactive status
  const fetchStudentsWithStatus = async () => {
    try {
      const { data: studentsData, error: studentsError } = await supabase
        .from('student_profile')
        .select('*')
        .order('last_name', { ascending: true });

      if (studentsError) throw studentsError;

      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];

      // Fetch all check-ins for today
      const { data: checkInsData, error: checkInsError } = await supabase
        .from('check_ins')
        .select('user_id, check_time')
        .gte('check_date', today)
        .lte('check_date', today);

      if (checkInsError) throw checkInsError;

      // Create a map of user_id to their latest check-in time
      const checkInMap = new Map<string, string>();
      (checkInsData || []).forEach((checkIn: any) => {
        const existingTime = checkInMap.get(checkIn.user_id);
        if (!existingTime || checkIn.check_time > existingTime) {
          checkInMap.set(checkIn.user_id, checkIn.check_time);
        }
      });

      // Map students with their status
      const studentsWithStatusData = (studentsData || []).map((student: StudentProfile) => {
        const hasCheckedInToday = checkInMap.has(student.id);
        return {
          ...student,
          status: hasCheckedInToday ? 'Active' : 'Inactive',
          lastCheckIn: checkInMap.get(student.id)
        } as StudentProfile & { status: 'Active' | 'Inactive', lastCheckIn?: string };
      });

      setStudentsWithStatus(studentsWithStatusData);

      // Count active students
      const activeCount = studentsWithStatusData.filter(s => s.status === 'Active').length;
      
      return activeCount;
    } catch (error) {
      console.error('Error fetching students with status:', error);
      return 0;
    }
  };

  // Handle opening the students modal - navigate to trainees with department tab
  const handleViewStudents = () => {
    navigate('/ojt-coordinator/trainees?tab=department');
  };

  // Handle opening the active students modal
  const handleViewActiveStudents = async () => {
    await fetchStudentsWithStatus();
    setShowActiveStudentsModal(true);
  };

  // Fetch and group students by class section
  const fetchStudentsBySection = async () => {
    try {
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .order('course_info', { ascending: true })
        .order('last_name', { ascending: true });

      if (error) throw error;

      // Group students by course_info (class section)
      const groupedStudents: { [section: string]: StudentProfile[] } = {};
      
      (studentsData || []).forEach((student: StudentProfile) => {
        const section = student.course_info || 'Unassigned';
        if (!groupedStudents[section]) {
          groupedStudents[section] = [];
        }
        groupedStudents[section].push(student);
      });

      setStudentsBySection(groupedStudents);
      return Object.keys(groupedStudents).length;
    } catch (error) {
      console.error('Error fetching students by section:', error);
      return 0;
    }
  };

  // Handle opening the class sections modal
  const handleViewClassSections = async () => {
    await fetchStudentsBySection();
    setShowClassSectionsModal(true);
  };

  // Handle opening the at-risk students modal
  const handleViewAtRiskStudents = async () => {
    // Refresh the data to ensure it's current
    await fetchWeeklyTrainees();
    setShowAtRiskStudentsModal(true);
  };


  // Sample data for pending tasks
  const pendingTasks = [
    { id: 1, name: 'Approve Student Registration', student: 'Maria Santos', date: '2025-09-08', status: 'pending' },
    { id: 2, name: 'Verify Weekly Report', student: 'John Dela Cruz', date: '2025-09-07', status: 'pending' },
    { id: 3, name: 'Review Completed Hours', student: 'Ana Reyes', date: '2025-09-06', status: 'completed' },
    { id: 4, name: 'Approve Location Change', student: 'Mike Rodriguez', date: '2025-09-05', status: 'pending' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/supervisor');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="ojt-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <a 
              href="#" 
              className={`nav-link ${activeView === 'home' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/ojt-coordinator/home');
              }}
            >
              <FaHome className="nav-icon" /> Home
            </a>
            <a 
              href="#" 
              className={`nav-link ${activeView === 'trainees' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/ojt-coordinator/trainees');
              }}
            >
              <FaUsers className="nav-icon" /> Trainees
            </a>
            <a 
              href="#" 
              className={`nav-link ${location.pathname === '/ojt-coordinator/feedback' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/ojt-coordinator/feedback');
              }}
            >
              <FaComments className="nav-icon" /> Feedback
            </a>
            <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Analytics</a>
            <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="ojt-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={(e) => {
          // Only close menu if clicking on a link, not on the sign out button
          if ((e.target as HTMLElement).closest('.logout-button')) {
            return;
          }
          setIsMenuOpen(false);
        }}>
          <a 
            href="#" 
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/ojt-coordinator/home');
            }}
          >
            <FaHome className="nav-icon" /> Home
          </a>
          <a 
            href="#" 
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/ojt-coordinator/trainees');
            }}
          >
            <FaUsers className="nav-icon" /> Trainees
          </a>
          <a 
            href="#" 
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              navigate('/ojt-coordinator/feedback');
            }}
          >
            <FaComments className="nav-icon" /> Feedback
          </a>
          <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Analytics</a>
          <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
        </nav>
        <div className="sidebar-footer">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }} 
            className="sidebar-logout-button"
          >
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        {activeView === 'home' ? (
          <>
            <div className="welcome-section">
              <h3 className="coordinator-title">OJT Coordinator Dashboard</h3>
              <h1>Welcome, {loading ? "Loading..." : `${userName.firstName} ${userName.lastName}`}</h1>
              <p className="welcome-message">
                As an OJT Coordinator, you play a crucial role in shaping the future of our interns. 
                Your guidance helps bridge academic learning with real-world experience, 
                preparing our students for successful careers. Today, you have {stats.atRiskStudents} at-risk 
                students that require your attention.
              </p>
            </div>
            
            <div className="dashboard-grid">
              <div className="panel">
                <div className="panel-header">Program Statistics</div>
                <div className="panel-content">
                  <div className="stats-grid">
                    <TotalStudentsCard 
                      count={stats.totalStudents} 
                      onClick={handleViewStudents} 
                    />
                    <ActiveStudentsCard 
                      count={stats.activeStudents} 
                      onClick={handleViewActiveStudents} 
                    />
                    <AtRiskStudentsCard 
                      count={stats.atRiskStudents} 
                      onClick={handleViewAtRiskStudents} 
                    />
                    <ClassSectionsCard 
                      count={stats.totalHours} 
                      onClick={handleViewClassSections} 
                    />
                  </div>
                </div>
              </div>

              <div className="panel tasks-panel">
                <div className="panel-header">Tasks Requiring Attention</div>
                <div className="panel-content">
                  {pendingTasks.map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-info">
                        <div className="task-name">{task.name}</div>
                        <div className="task-details">Student: {task.student} • Date: {task.date}</div>
                      </div>
                      <div className={`task-status ${task.status === 'pending' ? 'status-pending' : 'status-completed'}`}>
                        {task.status === 'pending' ? 'Pending' : 'Completed'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="trainees-section">
            <h3 className="coordinator-title">Trainee Management</h3>
            <TraineeList />
          </div>
        )}
      </main>

      {/* Class Sections Modal */}
      <ClassSectionsModal
        isOpen={showClassSectionsModal}
        onClose={() => setShowClassSectionsModal(false)}
        studentsBySection={studentsBySection}
      />

      {/* Active Students Modal */}
      <ActiveStudentsModal
        isOpen={showActiveStudentsModal}
        onClose={() => setShowActiveStudentsModal(false)}
        studentsWithStatus={studentsWithStatus}
      />

      {/* At Risk Students Modal */}
      <AtRiskStudentsModal
        isOpen={showAtRiskStudentsModal}
        onClose={() => setShowAtRiskStudentsModal(false)}
        weeklyTrainees={weeklyTrainees}
        loading={loading}
      />
    </div>
  );
};

export default OJTCoordinatorHome;
